import React, { useState, useCallback } from 'react';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { NeynarContextProvider, Theme } from '@neynar/react';
import studioTheme from './theme';
import AppBar from './components/AppBar';
import SearchService from './pages/SearchService';
import StoriesService from './pages/StoriesService';
import HydrationService from './pages/HydrationService';
import FeaturesService from './pages/FeaturesService';
import ScoringService from './pages/ScoringService';
import RankingService from './pages/RankingService';
import FeedBuilder from './pages/FeedBuilder';
import FeedsService from './pages/FeedsService';
import FeedConfigsService from './pages/FeedConfigsService';
import WarmupService from './pages/WarmupService';
import NotificationsService from './pages/NotificationsService';
import DashboardPage from './pages/DashboardPage';
import { GlobalProvider, useProduct } from './contexts/GlobalContext';
import { BuildProvider, useBuild } from './contexts/BuildContext';
import { DeployProvider, useDeploy } from './contexts/DeployContext';
import { EvalProvider, useEval } from './contexts/EvalContext';

const AppProviders = ({ children }) => (
  <GlobalProvider>
    <BuildProvider>
      <DeployProvider>
        <EvalProvider>
          {children}
        </EvalProvider>
      </DeployProvider>
    </BuildProvider>
  </GlobalProvider>
);

const BuildContent = ({ activeStep }) => {
  switch (activeStep) {
    case 0:
      return <SearchService />;
    case 1:
      return <StoriesService />;
    case 2:
      return <HydrationService />;
    case 3:
      return <FeaturesService />;
    case 4:
      return <ScoringService />;
    case 5:
      return <RankingService />;
    case 6:
      return <FeedBuilder />;
    default:
      return null;
  }
};

const DeployContent = ({ activeStep }) => {
  switch (activeStep) {
    case 0:
      return <FeedsService />;
    case 1:
      return <FeedConfigsService />;
    case 2:
      return <NotificationsService />;
    case 3:
      return <WarmupService />;
    default:
      return null;
  }
};

const EvalContent = ({ activeStep }) => {
  switch (activeStep) {
    case 0:
      return <DashboardPage />;
    default:
      return null;
  }
};

const AppContent = () => {
  const { activeProduct, setActiveProduct } = useProduct();
  const { buildSteps } = useBuild();
  const { deploySteps } = useDeploy();
  const { evalSteps } = useEval();

  const [activeStep, setActiveStepState] = useState(0);

  const steps =
    activeProduct === 'build' ? buildSteps
      : activeProduct === 'deploy' ? deploySteps
        : evalSteps;

  const setActiveStep = useCallback((step) => {
    const stepConfig = steps[step];
    if (stepConfig && stepConfig.enabled) {
      setActiveStepState(step);
    }
  }, [steps]);

  const handleProductChange = useCallback((product) => {
    setActiveProduct(product);
    setActiveStepState(0);
  }, [setActiveProduct]);

  const renderContent = () => {
    if (activeProduct === 'build') return <BuildContent activeStep={activeStep} />;
    if (activeProduct === 'deploy') return <DeployContent activeStep={activeStep} />;
    if (activeProduct === 'eval') return <EvalContent activeStep={activeStep} />;
    return null;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        activeProduct={activeProduct}
        setActiveProduct={handleProductChange}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={steps}
      />
      {renderContent()}
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={studioTheme}>
      <CssBaseline />
      <NeynarContextProvider
        settings={{
          clientId: "9af4d2c3-218f-4ce6-8cc8-5024d0065e28",
          defaultTheme: Theme.Light,
          eventsCallbacks: {
            onAuthSuccess: () => {},
            onSignout: () => {},
            onError: (error) => {},
          },
        }}
      >
        <AppProviders>
          <AppContent />
        </AppProviders>
      </NeynarContextProvider>
    </ThemeProvider>
  );
}

export default App;
